/**
 * Query Keys
 * 
 * Centralized query keys for React Query.
 * Using a factory pattern for type-safe and organized keys.
 * 
 * @example
 * // In a hook:
 * useQuery({ queryKey: queryKeys.apps.myApps() })
 * 
 * @example
 * // For invalidation:
 * queryClient.invalidateQueries({ queryKey: queryKeys.apps.all })
 */

// =============================================================================
// APPS QUERY KEYS
// =============================================================================

export const queryKeys = {
  /**
   * Apps-related query keys
   */
  apps: {
    /** Base key for all apps queries */
    all: ['apps'] as const,

    /** Key for listing all apps */
    lists: () => [...queryKeys.apps.all, 'list'] as const,

    /** Key for user's authorized apps */
    myApps: () => [...queryKeys.apps.all, 'my-apps'] as const,

    /** Key for active apps only */
    active: () => [...queryKeys.apps.all, 'active'] as const,

    /** Key for a single app by ID */
    detail: (id: number) => [...queryKeys.apps.all, 'detail', id] as const,
  },

  /**
   * Auth-related query keys
   */
  auth: {
    /** Base key for all auth queries */
    all: ['auth'] as const,

    /** Key for current user */
    currentUser: () => [...queryKeys.auth.all, 'currentUser'] as const,

    /** Key for auth check */
    check: () => [...queryKeys.auth.all, 'check'] as const,
  },

  /**
   * Permissions-related query keys
   */
  permissions: {
    /** Base key for all permissions queries */
    all: ['permissions'] as const,

    /** Key for user's permissions */
    user: (userId: number) => [...queryKeys.permissions.all, 'user', userId] as const,
  },

  /**
   * Custom apps (My Apps) query keys
   */
  customApps: {
    all: ['custom-apps'] as const,
    myCustomApps: () => [...queryKeys.customApps.all, 'my-custom-apps'] as const,
  },

  /**
   * Custom app categories query keys
   */
  customAppCategories: {
    all: ['custom-app-categories'] as const,
    myCategories: () => [...queryKeys.customAppCategories.all, 'my-categories'] as const,
  },

  /**
   * Partners-related query keys
   */
  partners: {
    /** Base key for all partners queries */
    all: ['partners'] as const,

    /** Key for listing all partners */
    lists: () => [...queryKeys.partners.all, 'list'] as const,

    /** Key for partner search */
    search: (query: string) => [...queryKeys.partners.all, 'search', query] as const,

    /** Key for a single partner by ID */
    detail: (id: number) => [...queryKeys.partners.all, 'detail', id] as const,

    /** Key for partner by business ID */
    byBusinessId: (businessId: number) => [...queryKeys.partners.all, 'business', businessId] as const,

    /** Key for partners by franchise */
    byFranchise: (franchiseId: string) => [...queryKeys.partners.all, 'franchise', franchiseId] as const,

    /** Key for partners by area */
    byArea: (areaId: number) => [...queryKeys.partners.all, 'area', areaId] as const,
  },
} as const;

